import { useMemo, useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { LioUserRole, BrokerageUserRole, engineRegExp } from '@lio/rule-engine';
import { Dialog } from '@components/dialog';
import { TextField } from '@components/textField';
import { Select, Item } from '@components/select';
import { AutocompleteRenderInputParams, Grid, Typography } from '@mui/material';
import { UserGetResponse, UserPostRequestBody, UserPostResponse, UserRolePostRequestBody } from '@lio/api-types';
import { useGetBrokerage, useSearchPaginatedBrokerages } from '@services/brokerage';
import {
  useCreateUser,
  useCreateUserRole,
  useDeleteUser,
  useDeleteUserRole,
  useGetUser,
  useSearchUser,
  useUpdateUser,
} from '@services/user';
import { Autocomplete } from '@components/autocomplete';
import { areArraysEqual } from '@utils/arrays';
import { getInitialUserRole } from '@pages/PortalAdmin/UserManagement/AddEditUserModal.utils';
import { UserType, User, initialUser, roles } from '@pages/PortalAdmin/UserManagement/AddEditUserModal.constants';
import { ToastType } from '@components/toast/ToastType.enum';
import { useSnackbar } from 'notistack';

import { handleScrollToBottom } from '@utils/HandleScrollToBottom';
import { UserModalType, UserModalTypes } from '@pages/PortalAdmin/UserManagement/UserManagement.constants';
import { useQueryClient } from '@tanstack/react-query';
import Loading from '@components/Loading';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { RadioGroupField } from '@components/radioButton';

interface Props {
  handleClose: () => void;
  modalType: UserModalType;
  selectedUserId?: string;
  isAddBrokerageUser?: boolean;
  selectedBrokerage?: Item;
  handleNewBrokerageUser?: (newUser: UserPostResponse) => void;
}

export const AddEditUserModal = ({
  handleClose,
  modalType,
  selectedUserId,
  isAddBrokerageUser,
  selectedBrokerage,
  handleNewBrokerageUser,
}: Props) => {
  const { user: selectedUser, isLoading } = useGetUser(selectedUserId);
  const { brokerage: initialBrokerage } = useGetBrokerage({
    brokerageId: selectedUser?.brokerageUsers[0]?.brokerageId,
    isEnabled: Boolean(selectedUser?.brokerageUsers[0]?.brokerageId),
  });
  const [searchbarText, setSearchbarText] = useState<string>('');
  const {
    searchBrokerage,
    isLoading: isSearchBrokeragesLoading,
    fetchNextPage: loadMoreBrokerageResults,
  } = useSearchPaginatedBrokerages(true, {
    sort: '+name',
    pageLimit: 25,
    name: searchbarText,
  });
  const allBrokerages = useMemo(() => {
    const allBrokeragesFlat = [...(searchBrokerage?.pages?.flatMap((page) => page.items) ?? [])].map((item) => ({
      value: item.id,
      label: item.name,
    }));
    if (initialBrokerage && !allBrokeragesFlat.find((el) => el.value === initialBrokerage.id)) {
      return allBrokeragesFlat.concat([{ value: initialBrokerage.id, label: initialBrokerage.name }]);
    }
    return allBrokeragesFlat;
  }, [initialBrokerage, searchBrokerage?.pages]);
  const { createUser, isPending: isCreateUserLoading } = useCreateUser();
  const { deleteUserRole, isPending: isDeleteUserRoleLoading } = useDeleteUserRole();
  const { createUserRole, isPending: isCreateUserRoleLoading } = useCreateUserRole();
  const { updateUser } = useUpdateUser({ isEditingOwnProfile: false });
  const { enqueueSnackbar } = useSnackbar();
  const { deleteUser } = useDeleteUser();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User>(
    isAddBrokerageUser
      ? { ...initialUser, type: UserType.BrokerageUser, role: BrokerageUserRole.Producer, brokerage: selectedBrokerage }
      : initialUser,
  );
  const { email, firstName, lastName, role, brokerage, type, idpUserId } = user;
  const [isDataPreFilled, setIsDataPreFilled] = useState(false);
  const [isEmailEntryComplete, setIsEmailEntryComplete] = useState(false);
  const [inputValue, setInputValue] = useState<string>(user.email || '');
  const isEmailValid = useMemo(() => engineRegExp.email.regExp.test(user.email), [user.email]);
  const { user: userSearchData } = useSearchUser({ email: inputValue });
  const isDuplicateUser = () => {
    return userSearchData?.items
      ? userSearchData.items.some((item) => item.email.toLowerCase() === email.toLowerCase())
      : false;
  };
  const userAlreadyExists = isDuplicateUser();
  const emailFieldErrorState = isEmailEntryComplete && (!isEmailValid || userAlreadyExists);
  const emailErrorMessage = () => {
    if (emailFieldErrorState) {
      if (!isEmailValid) return email === '' ? 'Email address is required' : 'Must be a valid email address';
      if (userAlreadyExists) return 'A user with that email address already exists';
    }
    return '';
  };

  const debouncedSearchbarText = useDebouncedCallback((value) => {
    setSearchbarText(value);
  }, DEBOUNCE_DELAYS.TYPING);

  const debouncedInputValue = useDebouncedCallback((value) => {
    setInputValue(value);
    setIsEmailEntryComplete(true);
  }, DEBOUNCE_DELAYS.TYPING);

  const getUserType = (user: UserGetResponse) => {
    if (user?.isCustomerUser) {
      return UserType.CustomerUser;
    }
    if (user?.brokerageUsers?.length) {
      return UserType.BrokerageUser;
    }
    if (user?.lioUsers?.length) {
      return UserType.LIOUser;
    }
    return undefined;
  };

  useEffect(() => {
    const isBrokerageUser = selectedUser?.brokerageUsers?.length;

    if (!isDataPreFilled && selectedUser && (initialBrokerage || !isBrokerageUser)) {
      setIsDataPreFilled(true);
      setUser({
        firstName: selectedUser?.firstName ?? initialUser.firstName,
        lastName: selectedUser?.lastName ?? initialUser.lastName,
        email: selectedUser?.email ?? initialUser.email,
        role: getInitialUserRole(selectedUser),
        brokerage: initialBrokerage
          ? {
              value: initialBrokerage.id,
              label: initialBrokerage.name,
            }
          : undefined,
        idpUserId: selectedUser.idpUserId ?? undefined,
        type: getUserType(selectedUser),
      });
    }
  }, [isDataPreFilled, selectedUser, initialBrokerage]);

  const actionMap = useMemo(
    () => ({
      [UserModalTypes.CreateUser]: async () => {
        try {
          if (type === UserType.BrokerageUser) {
            const brokerageId = brokerage?.value;
            const requestBody = {
              email,
              firstName,
              lastName,
              lioUser: undefined,
              brokerageUser: role !== undefined && brokerageId !== undefined ? { role, brokerageId } : undefined,
            } as UserPostRequestBody;
            const newUser = await createUser(requestBody);
            if (handleNewBrokerageUser) {
              handleNewBrokerageUser(newUser);
            }
          } else if (type === UserType.CustomerUser) {
            const requestBody = {
              email,
              firstName,
              lastName,
              lioUser: undefined,
              brokerageUser: undefined,
              isCustomerUser: true,
            } as UserPostRequestBody;
            const newUser = await createUser(requestBody);
            if (handleNewBrokerageUser) {
              handleNewBrokerageUser(newUser);
            }
          } else {
            const firstRole = (role as string[])[0];
            const otherRoles = (role as string[]).slice(1);
            const requestBody = {
              email,
              firstName,
              lastName,
              lioUser: { role: firstRole as LioUserRole },
              idpUserId,
              brokerageUser: undefined,
            } as UserPostRequestBody;
            await createUser(requestBody).then(async (newUser) => {
              Promise.all(
                otherRoles.map((role) =>
                  createUserRole({
                    requestBody: {
                      lioUser: { role },
                      brokerageUser: undefined,
                    } as UserRolePostRequestBody,
                    requestParams: {
                      userId: newUser.id,
                    },
                  }),
                ),
              );
            });
          }
        } catch (error: unknown) {
          const data = await (
            error as { response?: { json: () => Promise<{ error?: { message: string } }> } }
          )?.response?.json();
          enqueueSnackbar(`User Creation Failed: ${data?.error?.message}`, {
            persist: true,
            variant: ToastType.Error,
          });
        } finally {
          handleClose();
        }
      },
      [UserModalTypes.EditUser]: async () => {
        try {
          if (type === UserType.BrokerageUser) {
            const deleteUserRoleRequestParams = {
              userId: selectedUser?.id ?? '',
              role: getInitialUserRole(selectedUser) as string,
              brokerageId: type === UserType.BrokerageUser && initialBrokerage ? initialBrokerage.id : undefined,
            };
            const createUserRoleRequestBody = {
              lioUser: undefined,
              brokerageUser:
                type === UserType.BrokerageUser && role !== undefined && brokerage !== undefined
                  ? { role, brokerageId: brokerage.value }
                  : undefined,
            } as UserRolePostRequestBody;
            if (getInitialUserRole(selectedUser).length) {
              await deleteUserRole(deleteUserRoleRequestParams);
            }
            await createUserRole({
              requestBody: createUserRoleRequestBody,
              requestParams: {
                userId: selectedUser?.id ?? '',
              },
            });
          } else if (type === UserType.LIOUser) {
            const initialRoles = getInitialUserRole(selectedUser) as string[];
            const rolesToAdd = (role as string[]).filter((el) => !initialRoles.includes(el));
            const rolesToDelete = initialRoles.filter((el) => !(role as string[]).includes(el));
            if (rolesToDelete.length > 0) {
              Promise.all(
                rolesToDelete.map((role) =>
                  deleteUserRole({
                    userId: selectedUser?.id ?? '',
                    role,
                    brokerageId: undefined,
                  }),
                ),
              );
            }
            if (rolesToAdd.length > 0) {
              Promise.all(
                rolesToAdd.map((role) =>
                  createUserRole({
                    requestBody: {
                      lioUser: { role },
                      brokerageUser: undefined,
                    } as UserRolePostRequestBody,
                    requestParams: {
                      userId: selectedUser?.id ?? '',
                    },
                  }),
                ),
              );
            }
          }
          await updateUser({
            requestParams: { userId: selectedUserId ?? '' },
            requestBody: {
              idpUserId: idpUserId ?? null,
              isCustomerUser: type === UserType.CustomerUser,
            },
          });
        } catch (err) {
          enqueueSnackbar('User update failed', {
            persist: true,
            variant: ToastType.Error,
          });
        } finally {
          handleClose();
        }
      },
    }),
    // eslint-disable-next-line
    [
      brokerage,
      createUserRole,
      createUser,
      deleteUserRole,
      email,
      idpUserId,
      enqueueSnackbar,
      firstName,
      handleClose,
      handleNewBrokerageUser,
      initialBrokerage,
      lastName,
      role,
      selectedUserId,
      selectedUser,
      type,
      userAlreadyExists,
    ],
  );

  const tertiaryBtnAction = async () => {
    await handleDeactivateUser();
    handleClose();
  };

  const handleDeactivateUser = async () => {
    if (selectedUser?.id) {
      await deleteUser({ userId: selectedUser?.id });
      if (selectedUser.brokerageUsers[0].brokerageId) {
        queryClient.invalidateQueries({
          queryKey: ['brokerageUser', { brokerageId: selectedUser.brokerageUsers[0].brokerageId }],
        });
      }
    }
  };

  const isSubmitEnabled: boolean = useMemo(() => {
    if (modalType === UserModalTypes.CreateUser) {
      const areFieldsFilled = Boolean(email !== '' && firstName !== '' && lastName !== '');
      if (type === UserType.BrokerageUser) {
        if (isAddBrokerageUser) {
          return Boolean(areFieldsFilled && !emailFieldErrorState && selectedBrokerage && role?.length > 0);
        }
        return Boolean(areFieldsFilled && !emailFieldErrorState && brokerage && role?.length > 0);
      }
      return areFieldsFilled && !emailFieldErrorState && (type === UserType.CustomerUser || role?.length > 0);
    }
    if (modalType === UserModalTypes.EditUser) {
      const initialRole = getInitialUserRole(selectedUser);

      if (type === UserType.LIOUser) {
        const isRoleFilled = Boolean(role.length > 0);
        const hasChanges =
          !areArraysEqual(role as string[], initialRole as string[]) || idpUserId !== selectedUser?.idpUserId;
        return Boolean(isRoleFilled && hasChanges);
      }
      if (type === UserType.BrokerageUser) {
        const isRoleFilled = Boolean(role);
        const hasChanges =
          role !== initialRole || brokerage?.value !== initialBrokerage?.id || idpUserId !== selectedUser?.idpUserId;
        return Boolean(isRoleFilled && hasChanges && brokerage);
      }
      return Boolean(type);
    }
    return true;
  }, [
    brokerage,
    email,
    emailFieldErrorState,
    idpUserId,
    firstName,
    initialBrokerage?.id,
    isAddBrokerageUser,
    lastName,
    modalType,
    role,
    selectedBrokerage,
    selectedUser,
    type,
  ]);

  const userIsActive = selectedUser?.deletedAt === null || selectedUser?.deletedAt === undefined;

  const useruserAuth = selectedUser?.lioUsers.length !== 0 || selectedUser?.brokerageUsers.length !== 0;

  const isRadioDisabled = modalType === UserModalTypes.EditUser && useruserAuth && userIsActive;

  const canDeactivate = modalType === UserModalTypes.EditUser && userIsActive;

  const handleChange = (field: string, value: string | Item | boolean | undefined) => {
    setUser((prevValue) => ({ ...prevValue, [field]: value }));
    if (field === 'email') debouncedInputValue(value as string);
  };

  const PrimaryActionLabel: Record<Partial<UserModalType>, string> = {
    'Create User': 'Add User',
    'Edit User': userIsActive ? 'Save User' : 'Reactivate User',
    'View Preferences': 'View Preferences',
  };

  return (
    <Dialog
      open={modalType !== undefined}
      size='medium'
      handleCloseDialog={handleClose}
      title={modalType}
      primaryBtnText={PrimaryActionLabel[modalType]}
      primaryBtnAction={userIsActive ? actionMap[modalType] : actionMap[UserModalTypes.CreateUser]}
      isPrimaryBtnDisabled={!isSubmitEnabled}
      isPrimaryBtnLoading={isCreateUserLoading || isDeleteUserRoleLoading || isCreateUserRoleLoading}
      secondaryBtnText='Cancel'
      secondaryBtnAction={handleClose}
      tertiaryBtnText={canDeactivate ? 'Deactivate user' : undefined}
      tertiaryBtnAction={tertiaryBtnAction}
    >
      <Grid container sx={{ padding: '20px', height: '100%', flexDirection: 'column', display: 'flex' }}>
        {!isLoading ? (
          <>
            <Grid
              container
              spacing={2}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  inputLabel='First Name'
                  sx={{ marginBottom: '20px' }}
                  value={firstName}
                  onChangeInput={(eventValue) => handleChange('firstName', eventValue)}
                  disabled={modalType === UserModalTypes.EditUser}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  inputLabel='Last Name'
                  sx={{ marginBottom: '20px' }}
                  value={lastName}
                  onChangeInput={(eventValue) => handleChange('lastName', eventValue)}
                  disabled={modalType === UserModalTypes.EditUser}
                />
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Grid size={{ xs: 6 }}>
                <TextField
                  disabled={modalType === UserModalTypes.EditUser}
                  error={emailFieldErrorState}
                  fullWidth
                  helperText={emailErrorMessage()}
                  inputLabel='Email'
                  onBlur={() => setIsEmailEntryComplete(true)}
                  onChangeInput={(eventValue) => {
                    handleChange('email', eventValue);
                  }}
                  sx={{ marginBottom: '20px' }}
                  value={email}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  inputLabel='IDP User ID'
                  onChangeInput={(eventValue) => {
                    handleChange('idpUserId', eventValue);
                  }}
                  sx={{ marginBottom: '20px' }}
                  value={idpUserId}
                />
              </Grid>
            </Grid>
            {modalType === UserModalTypes.EditUser && (
              <Grid size={{ xs: 6 }}>
                <TextField
                  disabled={true}
                  fullWidth
                  inputLabel='Portal User ID'
                  slotProps={{ input: { readOnly: true } }}
                  sx={{ marginBottom: '20px' }}
                  value={selectedUser?.id}
                />
              </Grid>
            )}
            <Grid
              sx={{
                mb: 2,
              }}
            >
              <RadioGroupField
                row
                value={type ?? null}
                onChange={(e) => {
                  setUser((prevValue) => {
                    return { ...prevValue, type: e.target.value as UserType, role: [] };
                  });
                }}
                options={
                  isAddBrokerageUser
                    ? [{ value: UserType.BrokerageUser, label: 'Brokerage User', disabled: isRadioDisabled }]
                    : [
                        { value: UserType.LIOUser, label: 'LIO User', disabled: isRadioDisabled },
                        { value: UserType.BrokerageUser, label: 'Brokerage User', disabled: isRadioDisabled },
                        { value: UserType.CustomerUser, label: 'Customer User', disabled: isRadioDisabled },
                      ]
                }
              />
            </Grid>
            {type && roles[type]?.length > 0 && (
              <Grid size={{ xs: 6 }}>
                <Select
                  id='Role'
                  fullWidth
                  multiple={type === UserType.LIOUser}
                  inputLabel='Role'
                  items={roles[type]}
                  onChange={(event) => handleChange('role', event.target.value)}
                  sx={{ marginBottom: '20px' }}
                  value={role ? (role as string) : ''}
                  disabled={isAddBrokerageUser}
                />
              </Grid>
            )}
            {type === UserType.BrokerageUser && (
              <Grid size={{ xs: 6 }}>
                <Autocomplete
                  id='Brokerage'
                  disableClearable={Boolean(!brokerage)}
                  disabled={isAddBrokerageUser}
                  fullWidth
                  slotProps={{
                    listbox: {
                      style: { maxHeight: '300px' },
                      onScroll: (e) => handleScrollToBottom(e, loadMoreBrokerageResults),
                    },
                  }}
                  loading={isSearchBrokeragesLoading}
                  onChange={(_, value, reason) => {
                    if (reason === 'clear') {
                      handleChange('brokerage', undefined);
                    } else if (reason === 'selectOption') {
                      handleChange('brokerage', value as Item);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (event) {
                      handleChange('brokerage', undefined);
                      debouncedSearchbarText(newInputValue);
                    }
                  }}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField {...params} inputLabel='Brokerage' slotProps={{ input: { ...params.InputProps } }} />
                  )}
                  renderOption={(props, option) =>
                    (option as Item)?.value === '' || (option as Item)?.hidden ? null : (
                      <li {...props} key={(option as Item)?.value}>
                        <Typography
                          variant='bodySmall'
                          sx={{
                            color: 'common.black',
                          }}
                        >
                          {(option as Item).label}
                        </Typography>
                      </li>
                    )
                  }
                  sx={{ marginBottom: '20px' }}
                  options={[...allBrokerages, { value: '', label: '' }]}
                  value={user.brokerage?.label ?? ''}
                  isOptionEqualToValue={(option, value) => option.label === (value as unknown as string)}
                />
              </Grid>
            )}
          </>
        ) : (
          <Loading />
        )}
      </Grid>
    </Dialog>
  );
};
